import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { SiteHeader } from "@/components/SiteHeader";
import { CheckYourInbox } from "@/components/CheckYourInbox";
import { authClient, sendMagicLink } from "@/lib/auth";
import { emailHasPurchase } from "@/lib/auth-eligibility.functions";

export const Route = createFileRoute("/signin")({
  validateSearch: (search: Record<string, unknown>): { email?: string } =>
    typeof search.email === "string" ? { email: search.email } : {},
  head: () => {
    const title = "Sign in - WHO KNEW";
    const description =
      "Sign in to WHO KNEW and we'll email you a sign-in link. No password. The link expires in an hour.";
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:url", content: "/signin" },
      ],
    };
  },
  component: SignIn,
});

const MONO = '"JetBrains Mono", monospace';
const SERIF = '"Cormorant Garamond", serif';
const COURSE_PATH =
  "/courses/business-and-accountancy/basic-business-accountancy/lessons/1.1";

function friendlyRequestError(raw: string): string {
  const m = raw.toLowerCase();
  if (m.includes("rate") || m.includes("only request") || m.includes("too many")) {
    return "Too many requests. Try again in a minute.";
  }
  if (m.includes("invalid") && m.includes("email")) {
    return "That doesn't look like a valid email.";
  }
  return "Couldn't send the link. Try again in a moment.";
}

type Phase = "request" | "sent";

function SignIn() {
  const { email: emailParam } = Route.useSearch();
  const [email, setEmail] = useState(emailParam ?? "");
  const [phase, setPhase] = useState<Phase>("request");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // If already signed in, skip straight to the course.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await authClient.auth.getSession();
      if (!cancelled && data.session) {
        window.location.replace(COURSE_PATH);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefill: ?email= first, then localStorage.
  useEffect(() => {
    if (emailParam) {
      setEmail(emailParam);
      return;
    }
    try {
      const last = window.localStorage.getItem("last_signed_in_email");
      if (last) setEmail((cur: string) => cur || last);
    } catch {
      // ignore
    }
  }, [emailParam]);

  const send = async (addr: string) => {
    setSubmitting(true);
    setError(null);
    // Gate: only existing paid customers may request a magic link from /signin.
    // New customers come in via Stripe Checkout, which separately creates
    // the purchases row and triggers a magic link.
    try {
      const eligibility = await emailHasPurchase({ data: { email: addr } });
      if (!eligibility.eligible) {
        setSubmitting(false);
        setError("not_a_customer");
        return;
      }
    } catch (err) {
      // Fail-open: if the eligibility check itself errors, fall through to
      // the normal magic-link path rather than locking real customers out.
      console.error("[signin] eligibility check failed:", err);
    }
    const res = await sendMagicLink(addr);
    setSubmitting(false);
    if (res.ok) {
      try {
        window.localStorage.setItem("last_signed_in_email", addr);
      } catch {
        // ignore
      }
      setPhase("sent");
    } else {
      setError(friendlyRequestError(res.error));
    }
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const addr = email.trim();
    if (!addr || submitting) return;
    await send(addr);
  };

  const tryDifferent = () => {
    setPhase("request");
    setEmail("");
    setError(null);
  };

  return (
    <div className="flex min-h-screen flex-col bg-paper text-ink">
      <SiteHeader />

      <main className="flex flex-1 items-center justify-center px-6 pt-[80px] md:pt-0">
        <div className="w-full max-w-[720px]" style={{ paddingBottom: "96px" }}>
          {phase === "sent" ? (
            <CheckYourInbox email={email} onTryDifferent={tryDifferent} />
          ) : (
            <>
              <p
                className="text-[11px] font-bold uppercase tracking-[0.05em] text-burgundy"
                style={{ fontFamily: MONO }}
              >
                SIGN IN
              </p>
              <h1 className="mt-6 text-[28px] md:text-[44px] font-bold leading-[1.15] text-ink">
                Sign in.
              </h1>
              <hr className="mt-6 w-[60px] border-0 h-[2px] bg-burgundy" />
              <p
                className="mt-6 text-[18px] italic leading-[1.5]"
                style={{ color: "#5A534A", fontFamily: SERIF }}
              >
                We'll email you a sign-in link. No password. Expires in an hour.
              </p>

              <form onSubmit={onSubmit} className="mt-8">
                <label
                  className="block font-bold uppercase tracking-[0.05em]"
                  style={{
                    fontFamily: MONO,
                    fontSize: "10px",
                    color: "#5A534A",
                    marginBottom: "8px",
                  }}
                >
                  EMAIL
                </label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="block w-full bg-paper-dark px-5 py-4 text-[16px] text-ink placeholder:text-muted-foreground focus:outline-none"
                  style={{ fontFamily: MONO }}
                />
                <button
                  type="submit"
                  disabled={submitting}
                  className="interactive-lift mt-4 block w-full bg-burgundy px-6 py-[18px] text-[14px] font-bold uppercase tracking-[0.05em] text-paper transition-colors hover:bg-[#6B1717] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: MONO }}
                >
                  {submitting ? "SENDING…" : "SEND LINK"}
                </button>
                <p
                  className="mt-4 text-center text-[10px] font-bold uppercase tracking-[0.05em]"
                  style={{ color: "#5A534A", fontFamily: MONO }}
                >
                  NO PASSWORD · NO SPAM
                </p>
                {error === "not_a_customer" ? (
                  <p
                    className="mt-4 text-center text-[14px] italic"
                    style={{ color: "#5A534A", fontFamily: SERIF }}
                  >
                    We don't recognise that email. If you'd like to take the
                    course,{" "}
                    <Link
                      to="/"
                      className="italic text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2"
                    >
                      head to the homepage
                    </Link>{" "}
                    to get started.
                  </p>
                ) : error ? (
                  <p
                    className="mt-4 text-center text-[14px] italic"
                    style={{ color: "#5A534A", fontFamily: SERIF }}
                  >
                    {error}
                  </p>
                ) : null}
              </form>

              <div
                style={{
                  marginTop: "48px",
                  paddingTop: "32px",
                  borderTop: "1px solid #E8E1D3",
                }}
              >
                <p
                  className="font-bold uppercase tracking-[0.05em] text-burgundy"
                  style={{ fontFamily: MONO, fontSize: "11px" }}
                >
                  HAVEN'T MADE AN ACCOUNT?
                </p>
                <p
                  className="text-ink"
                  style={{ fontFamily: SERIF, fontSize: "18px", marginTop: "12px" }}
                >
                  <Link
                    to="/courses/business-and-accountancy/basic-business-accountancy"
                    className="text-burgundy underline decoration-burgundy/40 decoration-1 underline-offset-4 hover:decoration-2"
                  >
                    Go to the course
                  </Link>
                </p>
              </div>
            </>
          )}
        </div>
      </main>

      <footer className="flex w-full flex-col items-center">
        <hr className="border-0 h-px bg-burgundy w-full max-w-[720px]" />
        <p
          className="mt-6 font-bold uppercase tracking-[0.1em] text-burgundy text-[16px] text-center"
          style={{ fontFamily: MONO }}
        >
          WHO KNEW
        </p>
        <p
          className="mt-3 font-bold uppercase tracking-[0.05em] text-[10px] text-muted-foreground text-center"
          style={{ fontFamily: MONO }}
        >
          © 2026 WHO KNEW. ALL RIGHTS RESERVED.
        </p>
        <div
          className="mt-4 mb-6 font-bold uppercase tracking-[0.05em] text-[10px] text-burgundy text-center"
          style={{ fontFamily: MONO }}
        >
          <Link to="/privacy" className="link-hit link-hit-tight hover:underline">
            Privacy
          </Link>
          <span className="mx-2">·</span>
          <Link to="/terms" className="link-hit link-hit-tight hover:underline">
            Terms
          </Link>
          <span className="mx-2">·</span>
          <Link to="/refunds" className="link-hit link-hit-tight hover:underline">
            Refunds
          </Link>
          <span className="mx-2">·</span>
          <Link to="/teams" className="link-hit link-hit-tight hover:underline">
            For teams
          </Link>
        </div>
      </footer>
    </div>
  );
}
